import React, { ChangeEvent, FormEvent, useState } from 'react';
import { FaFileAlt } from 'react-icons/fa';
import { sendPartnerEmail } from '../../lib/api';

interface FormValues {
  fullName: string;
  workEmail: string;
  phone: string;
  company: string;
  website: string;
  locations: string;
  hours: string;
  startDate: string;
  message: string;
  marketingConsent: boolean;
}

interface FormState {
  values: FormValues;
  errors: Partial<Record<keyof FormValues, string | null>>;
}

const initValues: FormValues = {
  fullName: '',
  workEmail: '',
  phone: '',
  company: '',
  website: '',
  locations: '',
  hours: '',
  startDate: '',
  message: '',
  marketingConsent: false,
};

const initState: FormState = { values: initValues, errors: {} };

const isValidEmail = (email: string): boolean => {
  if (!email) return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};

const isValidPhone = (phone: string): boolean =>
  /^[\d+()\-\s]{7,20}$/.test((phone || '').trim());

type CoverageRequestFormProps = {
  id?: string;
};

const CoverageRequestForm: React.FC<CoverageRequestFormProps> = ({
  id = 'ContactPartner',
}) => {
  const [state, setState] = useState<FormState>(initState);
  const { values, errors } = state;
  const [success, setSuccess] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleState = ({
    target,
  }: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setState((prev) => ({
      ...prev,
      values: {
        ...prev.values,
        [target.name]:
          target.type === 'checkbox'
            ? (target as HTMLInputElement).checked
            : target.value,
      },
      errors: { ...prev.errors, [target.name]: null },
    }));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;

    const newErrors: Partial<Record<keyof FormValues, string | null>> = {};
    if (!values.fullName) newErrors.fullName = 'This field is required';
    if (!values.workEmail) newErrors.workEmail = 'This field is required';
    if (!values.phone) newErrors.phone = 'This field is required';
    if (!values.company) newErrors.company = 'This field is required';
    if (!values.locations) newErrors.locations = 'This field is required';
    if (!values.hours) newErrors.hours = 'This field is required';
    if (!values.startDate) newErrors.startDate = 'This field is required';
    if (!values.marketingConsent) {
      newErrors.marketingConsent =
        'Please agree to receive marketing and communications from Docmoonlight.';
    }

    if (Object.keys(newErrors).length > 0) {
      setState((prev) => ({ ...prev, errors: newErrors }));
      setSuccess('');
      return;
    }

    if (!isValidEmail(values.workEmail)) {
      setState((prev) => ({
        ...prev,
        errors: {
          ...prev.errors,
          workEmail: 'Please enter a valid email address.',
        },
      }));
      setSuccess('');
      return;
    }

    if (!isValidPhone(values.phone)) {
      setState((prev) => ({
        ...prev,
        errors: { ...prev.errors, phone: 'Please enter a valid phone number.' },
      }));
      setSuccess('');
      return;
    }

    const payload = {
      ...values,
      fullName: values.fullName.trim(),
      workEmail: values.workEmail.trim(),
      phone: values.phone.trim(),
      company: values.company.trim(),
      website: values.website.trim(),
      locations: values.locations.trim(),
      hours: values.hours.trim(),
      startDate: values.startDate.trim(),
      message: values.message.trim(),
      subject: 'Coverage Request Form',
    };

    try {
      setIsSubmitting(true);
      setSuccess('');
      const response = await sendPartnerEmail(payload);
      if (!response.ok) {
        throw new Error('Unable to submit this request.');
      }

      setSuccess('Your coverage request was submitted successfully.');
      setState(initState);
    } catch {
      setState((prev) => ({
        ...prev,
        errors: { ...prev.errors, message: 'Unable to submit this request.' },
      }));
      setSuccess('');
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputClass = (name: keyof FormValues) =>
    `w-full rounded-2xl border bg-white px-4 py-3 text-slate-900 shadow-sm transition placeholder:font-normal placeholder:text-slate-400 focus:outline-none focus:ring-2 ${
      errors[name]
        ? 'border-red-500 focus:ring-red-500'
        : 'border-[#333533]/16 focus:ring-[#f59e0b]'
    }`;

  const labelClass = (name: keyof FormValues) =>
    `mb-2 block text-sm font-semibold ${
      errors[name] ? 'text-red-500' : 'text-[#333533]'
    }`;

  return (
    <section className='px-4 py-10 sm:px-6 lg:px-8' id={id}>
      <div className='mx-auto max-w-7xl overflow-hidden rounded-[2.25rem] border border-[#333533]/14 bg-[linear-gradient(180deg,#fffef7_0%,#f0ede2_100%)] shadow-[0_25px_80px_rgba(32,32,32,0.08)]'>
        <div className='grid gap-0'>
          <div className='px-6 py-8 lg:px-8 lg:py-10'>
            <div className='mb-8 flex items-center gap-4'>
              <div className='rounded-full bg-[#f6c453]/30 p-3 text-[#202020]'>
                <FaFileAlt size={22} />
              </div>
              <div>
                <div className='text-sm font-bold uppercase tracking-[0.18em] text-[#202020]'>
                  Request details
                </div>
                <p className='mt-1 text-sm leading-6 text-[#333533]'>
                  Complete the form and we&apos;ll build a coverage plan that fits.
                </p>
              </div>
            </div>

            <form
              id={`${id}-form`}
              onSubmit={handleSubmit}
              className='scroll-mt-24 space-y-7'
            >
              <div className='grid grid-cols-1 gap-5 md:grid-cols-2'>
                <div>
                  <label htmlFor='fullName' className={labelClass('fullName')}>
                    Full name *
                  </label>
                  <input
                    id='fullName'
                    name='fullName'
                    value={values.fullName}
                    onChange={handleState}
                    className={inputClass('fullName')}
                  />
                  {errors.fullName && (
                    <p className='mt-2 text-xs text-red-500'>{errors.fullName}</p>
                  )}
                </div>

                <div>
                  <label htmlFor='workEmail' className={labelClass('workEmail')}>
                    Work email *
                  </label>
                  <input
                    id='workEmail'
                    name='workEmail'
                    value={values.workEmail}
                    onChange={handleState}
                    className={inputClass('workEmail')}
                    type='email'
                  />
                  {errors.workEmail && (
                    <p className='mt-2 text-xs text-red-500'>{errors.workEmail}</p>
                  )}
                </div>

                <div>
                  <label htmlFor='phone' className={labelClass('phone')}>
                    Phone *
                  </label>
                  <input
                    id='phone'
                    name='phone'
                    value={values.phone}
                    onChange={handleState}
                    className={inputClass('phone')}
                    type='tel'
                    placeholder='+1 (555) 123-4567'
                  />
                  {errors.phone && (
                    <p className='mt-2 text-xs text-red-500'>{errors.phone}</p>
                  )}
                </div>

                <div>
                  <label htmlFor='company' className={labelClass('company')}>
                    Company or practice *
                  </label>
                  <input
                    id='company'
                    name='company'
                    value={values.company}
                    onChange={handleState}
                    className={inputClass('company')}
                  />
                  {errors.company && (
                    <p className='mt-2 text-xs text-red-500'>{errors.company}</p>
                  )}
                </div>
              </div>

              <div className='grid grid-cols-1 gap-5 md:grid-cols-3'>
                <div>
                  <label htmlFor='website' className='mb-2 block text-sm font-semibold text-slate-700'>
                    Website
                  </label>
                  <input
                    id='website'
                    name='website'
                    value={values.website}
                    onChange={handleState}
                    className='w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-slate-900 shadow-sm transition placeholder:font-normal placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-500'
                  />
                </div>

                <div>
                  <label htmlFor='locations' className={labelClass('locations')}>
                    Number of locations *
                  </label>
                  <input
                    id='locations'
                    name='locations'
                    value={values.locations}
                    onChange={handleState}
                    className={inputClass('locations')}
                    type='number'
                  />
                  {errors.locations && (
                    <p className='mt-2 text-xs text-red-500'>{errors.locations}</p>
                  )}
                </div>

                <div>
                  <label htmlFor='hours' className={labelClass('hours')}>
                    Hours of operation *
                  </label>
                  <input
                    id='hours'
                    name='hours'
                    value={values.hours}
                    onChange={handleState}
                    className={inputClass('hours')}
                    placeholder='e.g. 7am - 9pm'
                  />
                  {errors.hours && (
                    <p className='mt-2 text-xs text-red-500'>{errors.hours}</p>
                  )}
                </div>
              </div>

              <div className='grid grid-cols-1 gap-5 md:grid-cols-[0.35fr_0.65fr]'>
                <div>
                  <label htmlFor='startDate' className={labelClass('startDate')}>
                    Desired start date *
                  </label>
                  <input
                    id='startDate'
                    name='startDate'
                    type='date'
                    value={values.startDate}
                    onChange={handleState}
                    className={inputClass('startDate')}
                  />
                  {errors.startDate && (
                    <p className='mt-2 text-xs text-red-500'>{errors.startDate}</p>
                  )}
                </div>

                <div>
                  <label htmlFor='message' className='mb-2 block text-sm font-semibold text-slate-700'>
                    Coverage notes
                  </label>
                  <textarea
                    id='message'
                    name='message'
                    value={values.message}
                    onChange={handleState}
                    placeholder='Share modalities, staffing gaps, states, scheduling windows, or any operational context.'
                    rows={4}
                    className='w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-slate-900 shadow-sm transition placeholder:font-normal placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-500'
                  />
                </div>
              </div>

              <div className='rounded-[1.5rem] border border-slate-200 bg-slate-50 p-4'>
                <div className='flex items-start gap-3'>
                  <input
                    id='marketingConsent'
                    type='checkbox'
                    name='marketingConsent'
                    checked={values.marketingConsent}
                    onChange={handleState}
                    className='mt-1 h-4 w-4 rounded border-slate-300 text-sky-600 focus:ring-sky-600'
                  />
                  <label htmlFor='marketingConsent' className='text-sm leading-7 text-slate-700'>
                    I agree to receive marketing and communications from{' '}
                    <strong>Docmoonlight</strong>.
                  </label>
                </div>
                {errors.marketingConsent && (
                  <p className='mt-2 text-xs text-red-500'>
                    {errors.marketingConsent}
                  </p>
                )}
              </div>

              {Object.keys(errors).length > 0 && (
                <p className='text-sm text-red-500'>
                  Please fill in all required fields correctly.
                </p>
              )}
              {success && <p className='text-sm text-green-600'>{success}</p>}

              <div className='flex flex-col gap-4 text-center md:flex-row md:items-center md:justify-between md:text-left'>
                <button
                  type='submit'
                  disabled={isSubmitting}
                  className='inline-flex w-full items-center justify-center gap-2 rounded-full bg-slate-900 px-6 py-3 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-70 sm:w-auto'
                >
                  {isSubmitting && (
                    <span className='h-4 w-4 animate-spin rounded-full border-2 border-white/35 border-t-white' />
                  )}
                  {isSubmitting ? 'Sending...' : 'Request my quote'}
                </button>
                <p className='max-w-md text-sm leading-7 text-slate-500'>
                  We use your information only to respond to your request. No
                  spam, no generic outreach, and no unnecessary follow-up.
                </p>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CoverageRequestForm;
